/**
 * Gift Archetype Worlds — FALLBACK config.
 *
 * The live source of truth is the backend `archetypes` collection served via
 * `GET /api/archetypes`. This static config exists so the site still renders
 * during the initial fetch and if the backend is unreachable. Fields mirror
 * the backend model (snake_case fields are converted for React consumption
 * in normalizeArchetype).
 *
 * Each archetype varies along exactly THREE visual axes: accent color,
 * title typography, hero style. Everything else stays Mementa.
 */

export const ARCHETYPES = [
  {
    id: 'seed-curated',
    slug: 'curated',
    title: 'Curated Picks',
    subtitle: 'Mementa Signature',
    tagline: 'Hand-selected thoughtful gifts by the Mementa AI Scout',
    fontFamily: "'Cormorant Garamond', serif",
    titleStyle: { fontWeight: 500, fontStyle: 'italic', letterSpacing: '-0.01em' },
    accentColor: '#FED7A5',
    accentColorDark: '#FED7A5',
    textAccent: '#9E6752',
    heroStyle: 'editorial',
    enabled: true,
    isDefault: true,
    isSystem: true,
    order: 0,
  },
  {
    id: 'seed-experiences',
    slug: 'experiences',
    title: 'Experiences',
    subtitle: 'Memories & Adventures',
    tagline: 'Unforgettable moments, escapes, and shared adventures',
    fontFamily: "'Syne', 'Space Grotesk', sans-serif",
    titleStyle: {
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    },
    accentColor: '#B45309',
    accentColorDark: '#FDBA74',
    textAccent: '#78350F',
    heroStyle: 'cinematic',
    enabled: true,
    order: 1,
  },
  {
    id: 'seed-handmade',
    slug: 'handmade',
    title: 'Handmade',
    subtitle: 'Artisan & Personalized',
    tagline: 'Crafted with patience by independent artisans — including custom and personalized keepsakes',
    fontFamily: "'Fraunces', serif",
    titleStyle: { fontWeight: 600, fontStyle: 'italic' },
    accentColor: '#9E6752',
    accentColorDark: '#D4A88E',
    textAccent: '#7C2D12',
    heroStyle: 'craft',
    enabled: true,
    order: 2,
  },
  {
    id: 'seed-diy',
    slug: 'diy_kits',
    title: 'DIY & Kits',
    subtitle: 'Craft & Creation',
    tagline: 'Curated tools and materials for hands-on creators',
    fontFamily: "'Outfit', sans-serif",
    titleStyle: { fontWeight: 800 },
    accentColor: '#73766A',
    accentColorDark: '#B4C5A9',
    textAccent: '#4B5563',
    heroStyle: 'workshop',
    enabled: true,
    order: 3,
  },
  {
    id: 'seed-support-a-brand',
    slug: 'support_a_brand',
    title: 'Support a Brand',
    subtitle: 'Small Businesses',
    tagline: 'A home for the makers, founders, and indie brands worth knowing',
    fontFamily: "'Bodoni Moda', serif",
    titleStyle: { fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
    accentColor: '#534145',
    accentColorDark: '#D4A4A8',
    textAccent: '#3F2F31',
    heroStyle: 'museum',
    enabled: true,
    order: 4,
  },
];

export const DEFAULT_ARCHETYPE_ID = 'curated';

/**
 * Normalize a backend archetype record into the frontend-friendly shape
 * (titleStyle sub-object, camelCase). Kept in one place so anywhere we hydrate
 * from the API uses the same conversion.
 */
export function normalizeArchetype(raw) {
  if (!raw) return null;
  const titleStyle = {};
  if (raw.font_weight ?? raw.fontWeight) {
    titleStyle.fontWeight = raw.font_weight ?? raw.fontWeight;
  }
  const style = raw.font_style ?? raw.fontStyle;
  if (style && style !== 'normal') titleStyle.fontStyle = style;
  const tt = raw.text_transform ?? raw.textTransform;
  if (tt && tt !== 'none') titleStyle.textTransform = tt;
  const ls = raw.letter_spacing ?? raw.letterSpacing;
  if (ls && ls !== '0') titleStyle.letterSpacing = ls;
  return {
    id: raw.id,
    slug: raw.slug,
    title: raw.title,
    subtitle: raw.subtitle || '',
    tagline: raw.tagline || '',
    fontFamily: raw.font_family ?? raw.fontFamily,
    titleStyle,
    accentColor: raw.accent_color ?? raw.accentColor,
    accentColorDark: raw.accent_color_dark ?? raw.accentColorDark,
    textAccent: raw.text_accent ?? raw.textAccent,
    heroStyle: raw.hero_style ?? raw.heroStyle,
    enabled: raw.enabled !== false,
    isDefault: raw.is_default ?? raw.isDefault ?? false,
    isSystem: raw.is_system ?? raw.isSystem ?? false,
    order: raw.order ?? 0,
  };
}

export function getEnabledArchetypes(list = ARCHETYPES) {
  return list.filter((a) => a.enabled);
}

export function getArchetype(slug, list = ARCHETYPES) {
  return list.find((a) => a.slug === slug || a.id === slug) || list[0];
}

export function getArchetypeAccent(archetype, isDark) {
  if (!archetype) return '#FED7A5';
  return isDark ? archetype.accentColorDark : archetype.accentColor;
}

/**
 * The accent shade to use for TEXT on light backgrounds. Some accents (peach,
 * yellow) are too light to be readable on white for eyebrows/labels. Each
 * archetype can override with `textAccent`; falls back to the regular accent.
 */
export function getArchetypeTextAccent(archetype, isDark) {
  if (!archetype) return '#9E6752';
  if (isDark) return archetype.accentColorDark;
  return archetype.textAccent || archetype.accentColor;
}
